
import React, { useState } from 'react';
import { View, Text, Button, ScrollView, Alert, StyleSheet } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Server Diagnostic Tool
 * Use this screen to test your server configuration
 */

const ServerDiagnostic = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (title, result) => {
    setResults(prev => [...prev, { title, result, timestamp: new Date().toISOString() }]);
  };

  const clearResults = () => {
    setResults([]);
  };

  // Test 1: Basic GET request to /health
  const testHealthEndpoint = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.jobeng.ai/health');
      const text = await response.text();
      
      addResult('1. Health Endpoint', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        response: text.substring(0, 500),
        length: text.length
      });
    } catch (error) {
      addResult('1. Health Endpoint - ERROR', {
        error: error.message,
        type: error.constructor.name
      });
    }
    setLoading(false);
  };

  // Test 2: Test GET request to /api/projects
  const testProjectsEndpoint = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.jobeng.ai/api/projects');
      const text = await response.text();
      
      addResult('2. Projects Endpoint', {
        status: response.status,
        statusText: response.statusText,
        length: text.length,
        preview: text.substring(0, 200)
      });
    } catch (error) {
      addResult('2. Projects Endpoint - ERROR', {
        error: error.message
      });
    }
    setLoading(false);
  };

  // Test 3: Test GET request to /expenses/1
  const testExpensesEndpoint = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.jobeng.ai/expenses/1');
      const text = await response.text();
      
      addResult('3. Expenses Endpoint', {
        status: response.status,
        statusText: response.statusText,
        length: text.length,
        preview: text.substring(0, 200)
      });
    } catch (error) {
      addResult('3. Expenses Endpoint - ERROR', {
        error: error.message
      });
    }
    setLoading(false);
  };

  // Test 4: Test OPTIONS request to /api/auth/login (CORS preflight)
  const testCORSPreflight = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.jobeng.ai/api/auth/login', {
        method: 'OPTIONS',
        headers: {
          'Origin': 'http://localhost:8080',
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'Content-Type'
        }
      });
      
      addResult('4. CORS Preflight (OPTIONS)', {
        status: response.status,
        statusText: response.statusText,
        headers: {
          'Access-Control-Allow-Origin': response.headers.get('Access-Control-Allow-Origin'),
          'Access-Control-Allow-Methods': response.headers.get('Access-Control-Allow-Methods'),
          'Access-Control-Allow-Headers': response.headers.get('Access-Control-Allow-Headers'),
        }
      });
    } catch (error) {
      addResult('4. CORS Preflight - ERROR', {
        error: error.message
      });
    }
    setLoading(false);
  };

  // Test 5: Test POST login with minimal headers (like curl)
  const testLoginPOST = async () => {
    setLoading(true);
    try {
      const loginData = {
        email: 'info@jobeng.in',
        password: 'yourpassword' // You'll need to enter actual password
      };
      
      const response = await fetch('https://api.jobeng.ai/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(loginData),
      });
      
      const text = await response.text();
      
      addResult('5. POST Login (like curl)', {
        status: response.status,
        statusText: response.statusText,
        length: text.length,
        response: text.substring(0, 500),
        headers: {
          'Content-Type': response.headers.get('Content-Type'),
          'Content-Length': response.headers.get('Content-Length'),
        }
      });
    } catch (error) {
      addResult('5. POST Login - ERROR', {
        error: error.message
      });
    }
    setLoading(false);
  };

  // Test 6: Test POST login with CORS mode
  const testLoginWithCORS = async () => {
    setLoading(true);
    try {
      const loginData = {
        email: 'info@jobeng.in',
        password: 'yourpassword'
      };
      
      const response = await fetch('https://api.jobeng.ai/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(loginData),
        mode: 'cors',
        credentials: 'omit'
      });
      
      const text = await response.text();
      
      addResult('6. POST Login (with CORS mode)', {
        status: response.status,
        statusText: response.statusText,
        length: text.length,
        response: text.substring(0, 500)
      });
    } catch (error) {
      addResult('6. POST Login with CORS - ERROR', {
        error: error.message
      });
    }
    setLoading(false);
  };

  // Test 7: Test with XMLHttpRequest (bypasses some CORS)
  const testLoginWithXHR = () => {
    setLoading(true);
    
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const url = 'https://api.jobeng.ai/api/auth/login';
      const loginData = {
        email: 'info@jobeng.in',
        password: 'yourpassword'
      };
      
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.timeout = 10000;
      
      xhr.onload = function() {
        addResult('7. XMLHttpRequest Login', {
          status: xhr.status,
          responseLength: xhr.responseText?.length || 0,
          responsePreview: xhr.responseText?.substring(0, 200) || 'empty',
          readyState: xhr.readyState
        });
        setLoading(false);
        resolve();
      };
      
      xhr.onerror = function() {
        addResult('7. XMLHttpRequest - ERROR', {
          error: 'Network error',
          readyState: xhr.readyState
        });
        setLoading(false);
        resolve();
      };
      
      xhr.ontimeout = function() {
        addResult('7. XMLHttpRequest - TIMEOUT', {
          error: 'Timeout',
          readyState: xhr.readyState
        });
        setLoading(false);
        resolve();
      };
      
      xhr.send(JSON.stringify(loginData));
    });
  };

  // Run all tests
  const runAllTests = async () => {
    clearResults();
    addResult('START', 'Starting server diagnostic tests...');
    
    await testHealthEndpoint();
    await testProjectsEndpoint();
    await testExpensesEndpoint();
    await testCORSPreflight();
    await testLoginPOST();
    await testLoginWithCORS();
    await testLoginWithXHR();
    
    addResult('COMPLETE', 'All tests completed');
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>🩺 Server Diagnostic Tool</Text>
      <Text style={styles.subtitle}>Testing: https://api.jobeng.ai</Text>
      
      <View style={styles.buttonContainer}>
        <Button 
          title="Run All Tests" 
          onPress={runAllTests} 
          disabled={loading}
          color="#4CAF50"
        />
        <Button 
          title="Clear Results" 
          onPress={clearResults} 
          color="#FF9800"
        />
      </View>
      
      <View style={styles.buttonContainer}>
        <Button title="Test Health" onPress={testHealthEndpoint} disabled={loading} />
        <Button title="Test CORS" onPress={testCORSPreflight} disabled={loading} />
        <Button title="Test Login POST" onPress={testLoginPOST} disabled={loading} />
      </View>
      
      <ScrollView style={styles.resultsContainer}>
        {results.map((item, index) => (
          <View key={index} style={styles.resultItem}>
            <Text style={styles.resultTitle}>{item.title}</Text>
            <Text style={styles.resultContent}>
              {typeof item.result === 'string' 
                ? item.result 
                : JSON.stringify(item.result, null, 2)}
            </Text>
            <Text style={styles.timestamp}>{item.timestamp}</Text>
          </View>
        ))}
      </ScrollView>
      
      {loading && (
        <View style={styles.loadingOverlay}>
          <Text style={styles.loadingText}>Testing server...</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#333',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 20,
    color: '#666',
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
  },
  resultsContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  resultItem: {
    backgroundColor: '#f9f9f9',
    padding: 15,
    marginBottom: 10,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
  },
  resultTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#333',
  },
  resultContent: {
    fontSize: 12,
    color: '#555',
    fontFamily: 'monospace',
  },
  timestamp: {
    fontSize: 10,
    color: '#999',
    marginTop: 5,
    textAlign: 'right',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default ServerDiagnostic;
